import { useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useState } from 'react'
import { useImmer } from 'use-immer'

import followUpFornecedorService from '@service/followUpFornecedor'

import {
  IAprovacaoContext,
  IAprovacaoItem,
  IAprovacaoParams
} from '@base/@types/providers/FollowUpFornecedor/AprovacaoProvider'

export const AprovacaoContext = createContext<IAprovacaoContext>(
  {} as IAprovacaoContext
)

const AprovacaoProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [currentyDate, setCurrentyDate] = useState<{
    initialDate: string
    finalDate: string
  }>({ initialDate: '', finalDate: '' })
  const [data, updateData] = useImmer<IAprovacaoItem[]>([])
  const [selected, updateSelected] = useImmer<IAprovacaoItem[]>([])
  const [modalAprovacao, setModalAprovacao] = useState<{
    open: boolean
    tipo: 1 | 2
  }>({ open: false, tipo: 1 })
  const [params, updateParams] = useImmer<IAprovacaoParams>({
    fornecedores: [] as any,
    centros: [] as any,
    gerenteProduto: [] as any,
    categoriaCompacta: [] as any,
    statusAprovacao: [],
    initialDate: '',
    finalDate: '',
    usuarios: [] as any
  })

  const buildBody = (p: IAprovacaoParams) => ({
    fornecedores: {
      ids: Array.isArray(p.fornecedores)
        ? (p.fornecedores as any[]).map((o: any) => Number(o.value))
        : (p.fornecedores as any).ids ?? []
    },
    centros: {
      ids: Array.isArray(p.centros)
        ? (p.centros as any[]).map((o: any) => Number(o.value))
        : (p.centros as any).ids ?? []
    },
    gerenteProduto: {
      ids: Array.isArray(p.gerenteProduto)
        ? (p.gerenteProduto as any[]).map((o: any) => Number(o.value))
        : (p.gerenteProduto as any)?.ids ?? []
    },
    categoriaCompacta: {
      ids: Array.isArray(p.categoriaCompacta)
        ? (p.categoriaCompacta as any[]).map((o: any) => Number(o.value))
        : (p.categoriaCompacta as any)?.ids ?? []
    },
    statusAprovacao: Array.isArray(p.statusAprovacao)
      ? p.statusAprovacao.map((o: any) =>
          typeof o === 'object' ? Number(o.value) : Number(o)
        )
      : [],
    dataInicio: p.initialDate || undefined,
    dataFim: p.finalDate || undefined,
    usuarios: {
      ids: Array.isArray((p as any).usuarios)
        ? ((p as any).usuarios as any[]).map((o: any) => Number(o.value))
        : (p.usuarios as any)?.ids ?? []
    }
  })

  const getData = async () => {
    setLoading(true)
    try {
      const { data: res } = await followUpFornecedorService.getAprovacao(
        buildBody(params)
      )
      updateData(res.data ?? [])
    } catch {
      updateData([])
      toast({
        title: 'Erro ao carregar dados de aprovação',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const { data: blob, headers } =
        await followUpFornecedorService.exportarAprovacao(buildBody(params))
      const disposition = headers['content-disposition'] || ''
      const match = disposition.match(/filename[^;=\n]*=(.*)/i)
      const filename = (
        match
          ? match[1].replace(/['"]/g, '').trim()
          : `Follow_Up_Fornecedor_Aprovacao_${new Date().toLocaleDateString(
              'pt-BR'
            )}.csv`
      ).replace(/\//g, '-')
      fileDownload(blob, filename)
    } catch (err: any) {
      let msg = 'Erro ao exportar'
      try {
        const body = err?.response?.data
        const json = body instanceof Blob ? JSON.parse(await body.text()) : body
        if (json?.message) msg = json.message
      } catch {}
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setExporting(false)
    }
  }

  const handleUpdateParams = (name: string, value: any) => {
    updateParams(draft => {
      ;(draft as any)[name] = value
    })
    if (name === 'initialDate') {
      setCurrentyDate(prev => ({ ...prev, initialDate: value }))
    }
    if (name === 'finalDate') {
      setCurrentyDate(prev => ({ ...prev, finalDate: value }))
    }
  }

  const handleToggleSelect = (item: IAprovacaoItem) => {
    updateSelected(draft => {
      const idx = draft.findIndex(i => i.id === item.id)
      if (idx >= 0) draft.splice(idx, 1)
      else draft.push(item)
    })
  }

  const handleSelectAll = () => {
    const pendentes = data.filter(i => i.statusAprovacao === 0)
    updateSelected(pendentes)
  }

  const handleClearSelection = () => updateSelected([])

  const handleOpenModal = (tipo: 1 | 2) => {
    setModalAprovacao({ open: true, tipo })
  }

  const handleCloseModal = () => setModalAprovacao({ open: false, tipo: 1 })

  const handleSingleAction = (item: IAprovacaoItem, tipo: 1 | 2) => {
    updateSelected([item])
    handleOpenModal(tipo)
  }

  const handleSubmitAprovacao = async (observacao: string) => {
    const itens = selected.map(i => ({
      itemId: i.id,
      statusAprovacao: modalAprovacao.tipo,
      observacaoAprovador: observacao || null
    }))
    try {
      await followUpFornecedorService.submitAprovacao(itens)
      const label = modalAprovacao.tipo === 1 ? 'Aprovação' : 'Recusa'
      toast({
        title: `${label} registrada com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      handleCloseModal()
      handleClearSelection()
      await getData()
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Erro ao processar aprovação'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  return (
    <AprovacaoContext.Provider
      value={{
        loading,
        data,
        params,
        currentyDate,

        selected,
        modalAprovacao,

        getData,
        handleUpdateParams,

        handleToggleSelect,
        handleSelectAll,

        handleClearSelection,
        handleOpenModal,

        handleCloseModal,
        handleSubmitAprovacao,

        handleSingleAction,

        exporting,
        handleExport
      }}
    >
      {children}
    </AprovacaoContext.Provider>
  )
}

export default AprovacaoProvider
